/**
 * Role-based path access.
 *
 * For most roles, route gating is handled by ProtectedRoute's existing
 * requiredRole hierarchy (viewer < operator < manager < admin). The
 * equipment_check role is special: it maps to viewer-level for the generic
 * hasRole() check (so it can read what viewer can read), but operationally
 * it should only see a narrow slice of the app. This module enumerates that
 * slice.
 *
 * Used by:
 *  - DashboardLayout, to filter the sidebar nav.
 *  - ProtectedRoute, to redirect disallowed direct-URL access to the role's
 *    landing page.
 */

import type { RoleType } from '../contexts/AuthContext';

// Allowed path prefixes for the equipment_check role.
// A path is allowed when it equals an entry exactly or starts with `entry + '/'`.
const EQUIPMENT_CHECK_ALLOWED: readonly string[] = [
  '/equipment-check',
  '/return-reports',
  '/issues',
  '/profile',
  // /setup-password is wrapped in ProtectedRoute (unlike the public auth
  // pages), so it must be allowed here — otherwise a first-time
  // equipment_check user bounces between the password guard and this
  // policy's landing-page redirect forever, rendering a blank page.
  '/setup-password',
];

/** Landing page for the role when redirecting from a disallowed path. */
export function landingPathForRole(role: RoleType | null | undefined): string {
  if (role === 'equipment_check') return '/equipment-check';
  return '/dashboard';
}

/**
 * Returns true iff a user with the given role(s) is allowed to access `path`
 * by the per-role path policy. Non-equipment_check roles return true; their
 * gating is performed by ProtectedRoute's role-hierarchy check elsewhere.
 */
export function isPathAllowedForRole(role: RoleType | null | undefined, path: string): boolean {
  if (role !== 'equipment_check') return true;
  return EQUIPMENT_CHECK_ALLOWED.some(
    (allowed) => path === allowed || path.startsWith(allowed + '/'),
  );
}
